<template>
  <button
    class="ms-button btn"
    :class="buttonClass"
    :disabled="disabled"
    type="button"
    @click="handleClick"
  >
    {{ text }}
  </button>
</template>

<script setup lang="ts">
import { defineProps, defineEmits, computed } from 'vue'

export type ButtonType = 'Primary' | 'Secondary' | 'Highlighted'

var props = defineProps({
  text: {
    type: String,
    required: true
  },
  disabled: {
    type: Boolean,
    default: false
  },
  buttonType: {
    type: String as () => ButtonType, // Set the type to ButtonType
    required: true
  }
})

const emit = defineEmits(['click'])

const handleClick = () => {
  emit('click')
}

// Compute the button class based on the buttonType prop
const buttonClass = computed(() => {
  switch (props.buttonType) {
    case 'Primary':
      return 'ms-btn-primary'
    case 'Secondary':
      return 'ms-btn-secondary'
    case 'Highlighted':
      return 'ms-btn-highlighted'
    default:
      return ''
  }
})
</script>

<style scoped lang="scss">
@use '@/styles/ms-color-styles.scss' as color;

.ms-button {
  border-radius: 0px;
  min-width: 90px;
  transition: background-color 0.2s ease, color 0.2s ease;
}

.ms-btn-primary {
  background-color: color.$color-nordic-blue-base;
  color: color.$color-white;
  border: 0px;
  padding-top: 8px;
  padding-bottom: 8px;
}

.ms-btn-primary:active {
  background-color: var(--color-primary-dark, #001a4d);
  color: #e0e0e0;
}

.ms-btn-primary:focus-visible {
  outline: 2px solid color.$color-nordic-blue-base;
}

.ms-btn-secondary {
  padding-top: 6px;
  padding-bottom: 6px;
  background-color: color.$color-white;
  color: color.$color-black;
  border: 2px solid color.$color-lighter-grey;
}

.ms-btn-secondary:active {
  background-color: var(--color-secondary-dark, #f0f0f0);
  color: var(--color-text-secondary, #555);
}

.ms-btn-secondary:focus-visible {
  outline: 2px solid color.$color-lighter-grey;
}

.ms-btn-highlighted {
  padding-top: 6px;
  padding-bottom: 6px;

  background-color: #5aa14f;
  color: white;
  border: 2px solid #5aa14f;
  &:hover {
    background-color: var(--color-highlighted, #457d3c);
    color: white;
    border: 2px solid var(--color-highlighted-border, #457d3c);
  }
}

.ms-btn-highlighted:active {
  background-color: var(--color-highlighted-dark, #337c28);
  color: white;
  border: 2px solid var(--color-highlighted-border, #337c28);
}

.ms-btn-highlighted:focus-visible {
  outline: 2px solid var(--color-highlighted, #337c28);
  color: white;
  border: 2px solid var(--color-highlighted-border, #337c28);
}
</style>
